import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface EnhancedSequencer {
  id: string;
  userId: string;
  displayName: string | null;
  username: string | null;
  avatarUrl: string | null;
  bio: string | null;
  socialLinks: Record<string, string> | null;
  sequenceCount: number;
  freeSequenceCount: number;
  paidSequenceCount: number;
  totalDownloads: number;
  averageRating: number;
  featured: boolean;
  verified: boolean;
  createdAt: string;
}

interface UseAllSequencersOptions {
  sortBy?: 'sequences' | 'downloads' | 'alphabetical' | 'newest';
  filterFeatured?: boolean;
  filterVerified?: boolean;
  filterFreeOnly?: boolean;
  holiday?: string;
  software?: string;
  genre?: string;
  limit?: number;
}

interface UseAllSequencersResult {
  sequencers: EnhancedSequencer[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch all sequencers (creators) with comprehensive aggregate statistics.
 *
 * Calculates per creator:
 * - Total sequences, free sequences, paid sequences
 * - Total downloads across all their sequences
 * - Average rating from reviews
 * - Featured and verified status
 *
 * Supports filtering by featured/verified status, free-only creators, and specialty filters.
 * Supports sorting by sequence count, downloads, alphabetical, or newest.
 *
 * @param options - Filtering and sorting options
 * @returns EnhancedSequencer array with loading and error states
 */
export function useAllSequencers(options: UseAllSequencersOptions = {}): UseAllSequencersResult {
  const {
    sortBy = 'sequences',
    filterFeatured = false,
    filterVerified = false,
    filterFreeOnly = false,
    holiday,
    software,
    genre,
    limit,
  } = options;

  const [sequencers, setSequencers] = useState<EnhancedSequencer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSequencers = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Build query for sequences with optional filters
        let sequencesQuery = supabase
          .from('sequences')
          .select('user_id, price, download_count, id, primary_genre')
          .not('user_id', 'is', null)
          .limit(10000); // Fetch all sequences (1000+ total)

        // Apply optional filters
        if (holiday) {
          sequencesQuery = sequencesQuery.eq('holiday_type', holiday);
        }
        if (software) {
          sequencesQuery = sequencesQuery.eq('software_type', software);
        }
        if (genre) {
          sequencesQuery = sequencesQuery.eq('primary_genre', genre);
        }

        const { data: sequences, error: sequencesError } = await sequencesQuery;

        console.log('🔍 [useAllSequencers] Fetched sequences:', sequences?.length || 0, 'Options:', options);

        if (sequencesError) {
          console.error('❌ [useAllSequencers] Error fetching sequences:', sequencesError);
          throw new Error(sequencesError.message);
        }

        if (!sequences || sequences.length === 0) {
          console.log('⚠️ [useAllSequencers] No sequences found, returning empty array');
          setSequencers([]);
          setIsLoading(false);
          return;
        }

        // Calculate per-user statistics
        const userStatsMap = new Map<
          string,
          {
            totalSequences: number;
            freeSequences: number;
            paidSequences: number;
            totalDownloads: number;
            sequenceIds: string[];
          }
        >();

        sequences.forEach((seq) => {
          if (!seq.user_id) return;

          const stats = userStatsMap.get(seq.user_id) || {
            totalSequences: 0,
            freeSequences: 0,
            paidSequences: 0,
            totalDownloads: 0,
            sequenceIds: [],
          };

          stats.totalSequences++;
          stats.sequenceIds.push(seq.id);
          stats.totalDownloads += seq.download_count || 0;

          // Check if free (price is null or 0)
          const price = seq.price ? parseFloat(seq.price.toString()) : 0;
          if (price === 0) {
            stats.freeSequences++;
          } else {
            stats.paidSequences++;
          }

          userStatsMap.set(seq.user_id, stats);
        });

        // Apply free-only filter if requested
        let userIds = Array.from(userStatsMap.keys());
        if (filterFreeOnly) {
          userIds = userIds.filter((userId) => {
            const stats = userStatsMap.get(userId);
            return stats && stats.paidSequences === 0;
          });
        }

        if (userIds.length === 0) {
          setSequencers([]);
          setIsLoading(false);
          return;
        }

        // Fetch user profiles
        let profilesQuery = supabase
          .from('user_profiles')
          .select('id, user_id, display_name, username, avatar_url, bio, social_links, featured, verified, created_at')
          .in('user_id', userIds);

        // Apply featured/verified filters
        if (filterFeatured) {
          profilesQuery = profilesQuery.eq('featured', true);
        }
        if (filterVerified) {
          profilesQuery = profilesQuery.eq('verified', true);
        }

        const { data: profiles, error: profilesError } = await profilesQuery;

        console.log('👤 [useAllSequencers] Fetched profiles:', profiles?.length || 0, 'from', userIds.length, 'user IDs');
        console.log('👤 [useAllSequencers] Filter featured:', filterFeatured, 'Filter verified:', filterVerified);

        if (profilesError) {
          console.error('❌ [useAllSequencers] Error fetching user profiles:', profilesError);
          throw new Error(profilesError.message);
        }

        if (!profiles || profiles.length === 0) {
          console.log('⚠️ [useAllSequencers] No profiles found, returning empty array');
          setSequencers([]);
          setIsLoading(false);
          return;
        }

        // Fetch average ratings for each user's sequences
        const allSequenceIds = Array.from(userStatsMap.values()).flatMap((s) => s.sequenceIds);
        const { data: reviews, error: reviewsError } = await supabase
          .from('reviews')
          .select('target_id, rating')
          .eq('target_type', 'sequence')
          .in('target_id', allSequenceIds);

        if (reviewsError) {
          console.error('Error fetching reviews:', reviewsError);
          // Don't throw - just continue without ratings
        }

        // Calculate average rating per user
        const userRatingsMap = new Map<string, number>();
        if (reviews && reviews.length > 0) {
          const sequenceRatings = new Map<string, number[]>();
          reviews.forEach((review) => {
            if (!sequenceRatings.has(review.target_id)) {
              sequenceRatings.set(review.target_id, []);
            }
            sequenceRatings.get(review.target_id)!.push(review.rating);
          });

          // Map sequence ratings to users
          userStatsMap.forEach((stats, userId) => {
            const allRatings: number[] = [];
            stats.sequenceIds.forEach((seqId) => {
              const ratings = sequenceRatings.get(seqId);
              if (ratings) {
                allRatings.push(...ratings);
              }
            });

            if (allRatings.length > 0) {
              const avgRating = allRatings.reduce((sum, r) => sum + r, 0) / allRatings.length;
              userRatingsMap.set(userId, Math.round(avgRating * 10) / 10); // Round to 1 decimal
            }
          });
        }

        // Combine all data
        const enhancedSequencers: EnhancedSequencer[] = profiles.map((profile) => {
          const stats = userStatsMap.get(profile.user_id)!;
          const avgRating = userRatingsMap.get(profile.user_id) || 0;

          return {
            id: profile.id,
            userId: profile.user_id,
            displayName: profile.display_name,
            username: profile.username,
            avatarUrl: profile.avatar_url,
            bio: profile.bio,
            socialLinks: profile.social_links as Record<string, string> | null,
            sequenceCount: stats.totalSequences,
            freeSequenceCount: stats.freeSequences,
            paidSequenceCount: stats.paidSequences,
            totalDownloads: stats.totalDownloads,
            averageRating: avgRating,
            featured: profile.featured || false,
            verified: profile.verified || false,
            createdAt: profile.created_at,
          };
        });

        // Sort sequencers based on sortBy option
        let sorted = enhancedSequencers;
        switch (sortBy) {
          case 'downloads':
            sorted = enhancedSequencers.sort((a, b) => b.totalDownloads - a.totalDownloads);
            break;
          case 'alphabetical':
            sorted = enhancedSequencers.sort((a, b) => {
              const nameA = (a.displayName || a.username || '').toLowerCase();
              const nameB = (b.displayName || b.username || '').toLowerCase();
              return nameA.localeCompare(nameB);
            });
            break;
          case 'newest':
            sorted = enhancedSequencers.sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
            break;
          case 'sequences':
          default:
            sorted = enhancedSequencers.sort((a, b) => b.sequenceCount - a.sequenceCount);
            break;
        }

        // Apply limit if specified
        const finalSequencers = limit ? sorted.slice(0, limit) : sorted;

        console.log(`✅ [useAllSequencers] Loaded ${finalSequencers.length} sequencers with options:`, options);
        console.log('✅ [useAllSequencers] First sequencer:', finalSequencers[0]);

        setSequencers(finalSequencers);
      } catch (err) {
        console.error('Error loading sequencers:', err);
        setError(err instanceof Error ? err.message : 'Failed to load sequencers');
        setSequencers([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadSequencers();
  }, [sortBy, filterFeatured, filterVerified, filterFreeOnly, holiday, software, genre, limit]);

  return {
    sequencers,
    isLoading,
    error,
  };
}
